import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Building2,
  ChevronRight,
  FileText,
  Landmark,
  Leaf,
  Map as MapIcon,
  MapPin,
  Mountain,
  Pencil,
  Printer,
  Receipt,
  ScrollText,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button, Modal, SectionHeader } from '@/ui/primitives';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { formatBRL, formatDateBR, formatName } from '@/lib/format';
import { useDocumentos, useDocumentosImovel, useUpdateImovel } from '../api';
import { formatCpf } from '../data';
import { OnusGravamesEditor } from './OnusGravamesEditor';
import { SoloPedologiaSection } from './SoloPedologiaSection';

interface Props {
  imovel: Record<string, unknown> | null;
  // null aceito pra suportar imóveis de PJ pura (sem cliente PF dono)
  cpfDono?: string | null;
  nomeDono?: string | null;
  onClose: () => void;
}

// Props pra ImovelDetailContent — mesmo set sem onClose (o wrapper que controla).
// Usado tanto pelo ImovelDetailModal direto quanto pelo ArrendamentoDetailModal
// (que precisa renderizar as 5 seções de Certidão/CAR/CCIR/Memorial/ITR de
// um imóvel arrendado dentro do próprio modal).
interface ContentProps {
  imovel: Record<string, unknown> | null;
  cpfDono?: string | null;
  nomeDono?: string | null;
}

type EditType = 'text' | 'date' | 'number' | 'select' | 'textarea';

interface EditableProps {
  field: string;
  type?: EditType;
  raw: string | number | null | undefined;
  display: ReactNode;
  options?: Array<[string, string]>;
}

interface UpdateCtx {
  patch: (data: Record<string, unknown>) => Promise<void>;
}
const UpdateContext = createContext<UpdateCtx | null>(null);

interface FieldDef {
  label: string;
  // ou um valor estático (read-only) ou um campo editável
  value?: ReactNode;
  edit?: EditableProps;
  group?: Array<{ subLabel?: string; edit: EditableProps }>;
  full?: boolean;
}

export function findItrReportDocument(
  documents: Array<Record<string, unknown>>,
  imovelId: string,
): Record<string, unknown> | null {
  const normalize = (value: unknown) =>
    String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  return (
    documents
      .filter((doc) => {
        const description = normalize(doc.descricao);
        const type = normalize(doc.tipo);
        return (
          String(doc.imovel_id ?? '') === imovelId &&
          normalize(doc.status || 'ativo') === 'ativo' &&
          (description.includes('relatorio de informacoes para elaboracao do itr') ||
            type.startsWith('relatorio_itr_'))
        );
      })
      .sort(
        (a, b) =>
          Date.parse(String(b.created_at ?? '')) - Date.parse(String(a.created_at ?? '')),
      )[0] ?? null
  );
}

// Wrapper Modal — mantém API pública estável pro ClienteResumo e qualquer
// outro consumer que abra o detalhe do imóvel como modal isolado.
export function ImovelDetailModal({ imovel: raw, cpfDono, nomeDono, onClose }: Props) {
  if (!raw) return null;
  const im = raw as Record<string, unknown> & { id?: string | number; [k: string]: unknown };
  const id = String(im.id ?? '');
  const nome = (im.denominacao as string) || `Imóvel #${id}`;
  return (
    <Modal open={true} onOpenChange={(v) => !v && onClose()} title={formatName(nome)} size="2xl">
      <ImovelDetailContent imovel={raw} cpfDono={cpfDono ?? null} nomeDono={nomeDono ?? null} />
    </Modal>
  );
}

// Renderiza só o conteúdo (sem Modal wrapper) — reusado pelo ArrendamentoDetailModal.
// Contém: Dados do Imóvel + 5 accordions (Certidão, CAR, Licença Ambiental, CCIR,
// Memorial, ITR, Sintegra). Edição click-to-edit funciona porque o PATCH bate
// direto em /api/v2/imoveis/:id — independente do contexto que renderiza.
export function ImovelDetailContent({ imovel: raw, cpfDono, nomeDono }: ContentProps) {
  const update = useUpdateImovel(cpfDono ?? null);
  const rawId =
    raw && typeof raw === 'object'
      ? String(raw.id ?? '')
      : '';
  const { data: documentosCliente = [] } = useDocumentos(cpfDono ?? null);
  const { data: documentosImovel = [] } = useDocumentosImovel(
    cpfDono ? null : rawId || null,
  );
  const documentos = cpfDono ? documentosCliente : documentosImovel;
  const fallbackDono =
    cpfDono || nomeDono ? { cpf: cpfDono ?? null, nome: nomeDono ?? null } : null;
  if (!raw) return null;
  const im = raw as Record<string, unknown> & {
    id?: string | number;
    [k: string]: unknown;
  };
  const id = String(im.id ?? '');
  const itrReport = findItrReportDocument(
    documentos as Array<Record<string, unknown>>,
    id,
  );
  const itrReportUrl = itrReport?.id
    ? `/api/v2/documentos/${encodeURIComponent(String(itrReport.id))}/arquivo`
    : null;
  const ctx: UpdateCtx = {
    patch: (data) =>
      new Promise((resolve, reject) => {
        update.mutate(
          { id, patch: data },
          {
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          },
        );
      }),
  };

  const get = (k: string) => im[k] as string | number | null | undefined;
  const dateRaw = (k: string) => {
    const v = get(k);
    return v ? String(v).slice(0, 10) : '';
  };

  return (
    <UpdateContext.Provider value={ctx}>
      <div>
        <SectionHeader icon={Building2} title="Dados do Imóvel" />
        <FieldGrid
          fields={[
            ed('CIB', 'cib', get('cib'), get('cib')),
            ed('INCRA', 'incra', get('incra'), get('incra')),
            ed('NIRF', 'nirf', get('nirf'), get('nirf')),
            {
              label: 'Município/UF',
              group: [
                {
                  edit: {
                    field: 'municipio',
                    raw: get('municipio'),
                    display: formatName(get('municipio')),
                  },
                },
                {
                  edit: {
                    field: 'uf',
                    raw: get('uf'),
                    display: get('uf'),
                  },
                },
              ],
            },
            {
              label: 'Tipo de Posse',
              full: true,
              edit: {
                field: 'tipo_posse',
                type: 'select',
                raw: get('tipo_posse'),
                display: get('tipo_posse'),
                options: [
                  ['', '— não informado —'],
                  ['Arrendamento rural', 'Arrendamento rural'],
                  ['Comodato rural', 'Comodato rural'],
                  ['Parceria rural', 'Parceria rural'],
                  ['Próprio', 'Próprio'],
                  ['Usufruto', 'Usufruto'],
                ],
              },
            },
            {
              label: 'Roteiro de Acesso',
              full: true,
              edit: {
                field: 'roteiro_acesso',
                raw: get('roteiro_acesso'),
                display: get('roteiro_acesso'),
              },
            },
          ]}
        />

        <Accordion
          icon={ScrollText}
          title="Certidão de Inteiro Teor"
          tone="indigo"
          subtitle={get('matricula') ? `Matrícula ${get('matricula')}` : null}
        >
          <FieldList
            fields={[
              ed('Nº da Matrícula', 'matricula', get('matricula'), get('matricula')),
              ed('Cartório', 'cartorio', get('cartorio'), formatName(get('cartorio'))),
              ed('Lote', 'lote', get('lote'), get('lote')),
              ed('Localização (Gleba)', 'gleba', get('gleba'), get('gleba')),
              ed(
                'Proprietário',
                'proprietario_nome',
                get('proprietario_nome'),
                formatName(get('proprietario_nome')),
              ),
              ed('CEP do Imóvel', 'cep_imovel', get('cep_imovel'), get('cep_imovel')),
              edNum(
                'Tamanho da Área (ha)',
                'area_total_ha',
                get('area_total_ha'),
                formatHa(get('area_total_ha')),
              ),
              edDate(
                'Data',
                'data_certidao',
                dateRaw('data_certidao'),
                safeDate(get('data_certidao')),
              ),
              edDate(
                'Validade',
                'validade_certidao',
                dateRaw('validade_certidao'),
                safeDate(get('validade_certidao')),
              ),
            ]}
          />

          <SubsectionLabel>Ônus / Gravames</SubsectionLabel>
          <OnusGravamesEditor
            raw={im.onus_items}
            legacyText={(im.onus as string | null | undefined) ?? null}
            onChange={(items) => ctx.patch({ onus_items: items })}
            context={{
              nomeDono,
              cpfDono,
              matricula: get('matricula'),
              cartorio: get('cartorio'),
              municipio: get('municipio'),
              uf: get('uf'),
              imovelNome: get('denominacao'),
              lote: get('lote'),
              gleba: get('gleba'),
              proprietarioNome: get('proprietario_nome'),
            }}
          />
        </Accordion>

        <Accordion
          icon={Leaf}
          title="CAR"
          tone="green"
          subtitle={(get('car_registro') as string) || (get('car_codigo') as string) || null}
        >
          {im.id != null && (
            <div className="mb-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  window.open(`/api/v2/imoveis/${im.id}/mapa-desenho`, '_blank', 'noreferrer')
                }
              >
                <MapIcon size={14} strokeWidth={1.5} />
                Ver mapa
              </Button>
            </div>
          )}

          <SubsectionLabel>CAR</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Registro no CAR',
                'car_registro',
                get('car_registro') || get('car_codigo'),
                get('car_registro') || get('car_codigo'),
              ),
              ed(
                'Nome do Imóvel Rural',
                'car_nome_imovel',
                get('car_nome_imovel'),
                formatName(get('car_nome_imovel')),
              ),
              ed('Município', 'municipio', get('municipio'), formatName(get('municipio'))),
              ed('UF', 'uf', get('uf'), get('uf')),
              ed('Latitude', 'car_latitude', get('car_latitude'), get('car_latitude')),
              ed('Longitude', 'car_longitude', get('car_longitude'), get('car_longitude')),
              edNum(
                'Área Total (ha)',
                'area_total_ha',
                get('area_total_ha'),
                formatHa(get('area_total_ha')),
              ),
            ]}
          />

          <SubsectionLabel>Identificação do Proprietário/Possuidor</SubsectionLabel>
          <PartyList
            items={im.car_proprietarios as Parameters<typeof PartyList>[0]['items']}
            fallback={fallbackDono}
          />

          <SubsectionLabel>Áreas</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Área Consolidada',
                'car_area_consolidada',
                get('car_area_consolidada'),
                get('car_area_consolidada'),
              ),
              ed(
                'Vegetação Nativa',
                'car_vegetacao_nativa',
                get('car_vegetacao_nativa'),
                get('car_vegetacao_nativa'),
              ),
              ed(
                'Reserva Legal',
                'car_reserva_legal',
                get('car_reserva_legal'),
                get('car_reserva_legal'),
              ),
              ed('Preservação Permanente', 'car_app', get('car_app'), get('car_app')),
              ed(
                'Número da Matrícula',
                'car_matricula',
                get('car_matricula'),
                get('car_matricula'),
              ),
            ]}
          />
        </Accordion>

        <Accordion
          icon={Mountain}
          title="Solo (Pedologia)"
          tone="amber"
          subtitle="Cruzamento RADAM/IBGE com polígono CAR"
        >
          <SoloPedologiaSection imovelId={(im.id as number | string | undefined) ?? null} />
        </Accordion>

        <Accordion
          icon={ShieldCheck}
          title="Licença Ambiental"
          tone="amber"
          subtitle={get('lic_ambiental_numero') ? `Nº ${get('lic_ambiental_numero')}` : null}
        >
          <FieldList
            fields={[
              ed(
                'Nº da Licença',
                'lic_ambiental_numero',
                get('lic_ambiental_numero'),
                get('lic_ambiental_numero'),
              ),
              ed(
                'Tipo de Licença',
                'lic_ambiental_tipo',
                get('lic_ambiental_tipo'),
                get('lic_ambiental_tipo'),
              ),
              ed(
                'Órgão Emissor',
                'lic_ambiental_orgao',
                get('lic_ambiental_orgao'),
                get('lic_ambiental_orgao'),
              ),
              ed(
                'Atividade',
                'lic_ambiental_atividade',
                get('lic_ambiental_atividade'),
                get('lic_ambiental_atividade'),
              ),
              edDate(
                'Data de Emissão',
                'lic_ambiental_data_emissao',
                dateRaw('lic_ambiental_data_emissao'),
                safeDate(get('lic_ambiental_data_emissao')),
              ),
              edDate(
                'Data de Validade',
                'lic_ambiental_data_validade',
                dateRaw('lic_ambiental_data_validade'),
                safeDate(get('lic_ambiental_data_validade')),
              ),
              {
                label: 'Condicionantes',
                full: true,
                edit: {
                  field: 'lic_ambiental_condicionantes',
                  type: 'textarea',
                  raw: get('lic_ambiental_condicionantes'),
                  display: get('lic_ambiental_condicionantes'),
                },
              },
            ]}
          />
        </Accordion>

        <Accordion
          icon={Landmark}
          title="CCIR"
          tone="purple"
          subtitle={get('ccir_numero') ? `Nº ${get('ccir_numero')}` : null}
        >
          <FieldList
            fields={[
              ed('Número do CCIR', 'ccir_numero', get('ccir_numero'), get('ccir_numero')),
              edDate(
                'Data de Lançamento',
                'ccir_data_lancamento',
                dateRaw('ccir_data_lancamento'),
                safeDate(get('ccir_data_lancamento')),
              ),
              ed(
                'Código do Imóvel Rural',
                'ccir_codigo_imovel',
                get('ccir_codigo_imovel'),
                get('ccir_codigo_imovel'),
              ),
              ed(
                'Nº de Módulos Rurais',
                'ccir_modulos_rurais',
                get('ccir_modulos_rurais'),
                get('ccir_modulos_rurais'),
              ),
              ed(
                'Nº de Módulos Fiscais',
                'ccir_modulos_fiscais',
                get('ccir_modulos_fiscais'),
                get('ccir_modulos_fiscais'),
              ),
              ed('Município', 'municipio', get('municipio'), formatName(get('municipio'))),
              edNum(
                'Área Total (ha)',
                'area_total_ha',
                get('area_total_ha'),
                formatHa(get('area_total_ha')),
              ),
              ed(
                'Matrícula/Transcrição',
                'ccir_matricula_transcricao',
                get('ccir_matricula_transcricao'),
                get('ccir_matricula_transcricao'),
              ),
              edDate(
                'Data de Geração',
                'ccir_data_geracao',
                dateRaw('ccir_data_geracao'),
                safeDate(get('ccir_data_geracao')),
              ),
              edDate(
                'Data de Vencimento',
                'ccir_data_vencimento',
                dateRaw('ccir_data_vencimento'),
                safeDate(get('ccir_data_vencimento')),
              ),
              ed('Status', 'ccir_status', get('ccir_status'), get('ccir_status')),
            ]}
          />

          <SubsectionLabel>Dados dos Titulares</SubsectionLabel>
          <PartyList
            items={im.ccir_titulares as Parameters<typeof PartyList>[0]['items']}
            fallback={fallbackDono}
          />
        </Accordion>

        <Accordion
          icon={MapPin}
          title="Memorial Descritivo"
          tone="accent"
          subtitle={get('sigef_codigo') ? `SIGEF ${get('sigef_codigo')}` : null}
        >
          <FieldList
            fields={[
              ed(
                'Denominação',
                'memorial_denominacao',
                get('memorial_denominacao') || get('denominacao'),
                get('memorial_denominacao') || get('denominacao'),
              ),
              ed(
                'Proprietário',
                'memorial_proprietario',
                get('memorial_proprietario'),
                formatName(get('memorial_proprietario')),
              ),
              ed(
                'Matrícula do Imóvel',
                'memorial_matricula',
                get('memorial_matricula') || get('matricula'),
                get('memorial_matricula') || get('matricula'),
              ),
              ed(
                'Município/UF',
                'memorial_municipio_uf',
                get('memorial_municipio_uf'),
                get('memorial_municipio_uf') ||
                  (get('municipio')
                    ? `${formatName(get('municipio'))}${get('uf') ? `/${get('uf')}` : ''}`
                    : null),
              ),
              edNum(
                'Área (ha)',
                'area_total_ha',
                get('area_total_ha'),
                formatHa(get('area_total_ha')),
              ),
              edNum(
                'Perímetro (m)',
                'perimetro_m',
                get('perimetro_m'),
                formatNum(get('perimetro_m')),
              ),
              ed('Datum', 'datum', get('datum'), get('datum')),
              ed('Cartório (CNS)', 'cartorio', get('cartorio'), formatName(get('cartorio'))),
              ed('INCRA/SNCR', 'incra', get('incra'), get('incra')),
              ed('Código SIGEF', 'sigef_codigo', get('sigef_codigo'), get('sigef_codigo')),
              ed(
                'Certificação',
                'memorial_certificacao',
                get('memorial_certificacao'),
                get('memorial_certificacao'),
              ),
              {
                label: 'Confrontações',
                full: true,
                edit: {
                  field: 'confrontacoes',
                  type: 'textarea',
                  raw: get('confrontacoes'),
                  display: get('confrontacoes'),
                },
              },
            ]}
          />
        </Accordion>

        <Accordion
          icon={Receipt}
          title="ITR — Recibo e Declaração"
          tone="sky"
          subtitle={get('itr_cib') || get('cib') ? `CIB ${get('itr_cib') || get('cib')}` : null}
          {...(itrReportUrl
            ? {
                onOpenReport: () => {
                  window.open(itrReportUrl, '_blank', 'noopener,noreferrer');
                },
                onPrint: () => {
                  window.open(itrReportUrl, '_blank', 'noopener,noreferrer');
                },
              }
            : {})}
        >
          {!itrReportUrl && (
            <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-text-2">
              Relatório de Informações para Elaboração do ITR indisponível. Anexe a certidão de
              inteiro teor e vincule o relatório técnico individual deste imóvel. A ficha cadastral
              não é utilizada como substituta.
            </div>
          )}
          <SubsectionLabel>Dados do Imóvel Rural</SubsectionLabel>
          <FieldList
            fields={[
              ed('CIB', 'itr_cib', get('itr_cib') || get('cib'), get('itr_cib') || get('cib')),
              ed(
                'Área Total',
                'itr_area_total',
                get('itr_area_total'),
                get('itr_area_total') || formatHa(get('area_total_ha')),
              ),
              ed(
                'Nome',
                'itr_nome_imovel',
                get('itr_nome_imovel') || get('denominacao'),
                formatName(get('itr_nome_imovel') || get('denominacao')),
              ),
              ed(
                'Endereço',
                'itr_endereco_imovel',
                get('itr_endereco_imovel'),
                get('itr_endereco_imovel'),
              ),
              ed(
                'Município',
                'itr_municipio_imovel',
                get('itr_municipio_imovel') || get('municipio'),
                formatName(get('itr_municipio_imovel') || get('municipio')),
              ),
              ed(
                'UF',
                'itr_uf_imovel',
                get('itr_uf_imovel') || get('uf'),
                get('itr_uf_imovel') || get('uf'),
              ),
              ed(
                'CEP',
                'itr_cep_imovel',
                get('itr_cep_imovel') || get('cep_imovel'),
                get('itr_cep_imovel') || get('cep_imovel'),
              ),
            ]}
          />

          <SubsectionLabel>Identificação do Contribuinte</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Nome',
                'itr_contrib_nome',
                get('itr_contrib_nome'),
                formatName(get('itr_contrib_nome')),
              ),
              ed(
                'CPF',
                'itr_contrib_cpf',
                get('itr_contrib_cpf'),
                get('itr_contrib_cpf') ? formatCpf(get('itr_contrib_cpf') as string) : null,
              ),
              ed(
                'Telefone',
                'itr_contrib_telefone',
                get('itr_contrib_telefone'),
                get('itr_contrib_telefone'),
              ),
              ed(
                'Endereço',
                'itr_contrib_endereco',
                get('itr_contrib_endereco'),
                get('itr_contrib_endereco'),
              ),
              ed(
                'Complemento',
                'itr_contrib_complemento',
                get('itr_contrib_complemento'),
                get('itr_contrib_complemento'),
              ),
              ed(
                'Bairro',
                'itr_contrib_bairro',
                get('itr_contrib_bairro'),
                formatName(get('itr_contrib_bairro')),
              ),
              ed(
                'Município',
                'itr_contrib_municipio',
                get('itr_contrib_municipio'),
                formatName(get('itr_contrib_municipio')),
              ),
              ed('UF', 'itr_contrib_uf', get('itr_contrib_uf'), get('itr_contrib_uf')),
              ed('CEP', 'itr_contrib_cep', get('itr_contrib_cep'), get('itr_contrib_cep')),
            ]}
          />

          <SubsectionLabel>Distribuição da Área Utilizada na Atividade Rural</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Produtos Vegetais',
                'itr_produtos_vegetais',
                get('itr_produtos_vegetais'),
                get('itr_produtos_vegetais'),
              ),
              ed('Pastagem', 'itr_pastagem', get('itr_pastagem'), get('itr_pastagem')),
            ]}
          />

          <SubsectionLabel>Cálculo do Valor da Terra Nua e do Imposto</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Valor Total do Imóvel',
                'itr_valor_total',
                get('itr_valor_total'),
                formatBRLOptional(get('itr_valor_total')),
              ),
            ]}
          />
        </Accordion>

        <Accordion
          icon={FileText}
          title="SINTEGRA"
          tone="amber"
          subtitle={
            get('sintegra_inscricao_estadual') ? `IE ${get('sintegra_inscricao_estadual')}` : null
          }
        >
          <SubsectionLabel>Identificação fiscal</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'CPF/CNPJ',
                'sintegra_cpf_cnpj',
                get('sintegra_cpf_cnpj'),
                get('sintegra_cpf_cnpj'),
              ),
              ed(
                'Inscrição Estadual',
                'sintegra_inscricao_estadual',
                get('sintegra_inscricao_estadual'),
                get('sintegra_inscricao_estadual'),
              ),
              ed('NIRE', 'sintegra_nire', get('sintegra_nire'), get('sintegra_nire')),
              ed(
                'Razão Social',
                'sintegra_razao_social',
                get('sintegra_razao_social'),
                formatName(get('sintegra_razao_social')),
              ),
              ed(
                'Nome Fantasia',
                'sintegra_nome_fantasia',
                get('sintegra_nome_fantasia'),
                formatName(get('sintegra_nome_fantasia')),
              ),
              ed(
                'Natureza Jurídica',
                'sintegra_natureza_juridica',
                get('sintegra_natureza_juridica'),
                get('sintegra_natureza_juridica'),
              ),
              ed(
                'UF de origem (SEFAZ)',
                'sintegra_uf_origem',
                get('sintegra_uf_origem'),
                get('sintegra_uf_origem'),
              ),
              edDate(
                'Consultado em',
                'sintegra_consulta_em',
                dateRaw('sintegra_consulta_em'),
                safeDate(get('sintegra_consulta_em')),
              ),
            ]}
          />

          <SubsectionLabel>Situação SEFAZ</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Regime de Pagamento',
                'sintegra_regime_pagamento',
                get('sintegra_regime_pagamento'),
                get('sintegra_regime_pagamento'),
              ),
              ed(
                'Situação Cadastral',
                'sintegra_situacao',
                get('sintegra_situacao'),
                get('sintegra_situacao'),
              ),
              edDate(
                'Data da Situação',
                'sintegra_data_situacao',
                dateRaw('sintegra_data_situacao'),
                safeDate(get('sintegra_data_situacao')),
              ),
              ed(
                'Situação do Contribuinte',
                'sintegra_situacao_contribuinte',
                get('sintegra_situacao_contribuinte'),
                get('sintegra_situacao_contribuinte'),
              ),
              edDate(
                'Início de Atividade',
                'sintegra_data_inicio',
                dateRaw('sintegra_data_inicio'),
                safeDate(get('sintegra_data_inicio')),
              ),
              ed('CNAE Principal', 'sintegra_cnae', get('sintegra_cnae'), get('sintegra_cnae')),
              ed(
                'Atividade Principal',
                'sintegra_atividade',
                get('sintegra_atividade'),
                formatName(get('sintegra_atividade')),
              ),
              ed(
                'Usuário de PED',
                'sintegra_usuario_ped',
                String(get('sintegra_usuario_ped') ?? ''),
                get('sintegra_usuario_ped') == null
                  ? ''
                  : get('sintegra_usuario_ped')
                    ? 'Sim'
                    : 'Não',
              ),
              {
                label: 'Regime de Apuração ICMS',
                full: true,
                edit: {
                  field: 'sintegra_regime_apuracao',
                  type: 'textarea',
                  raw: get('sintegra_regime_apuracao'),
                  display: get('sintegra_regime_apuracao'),
                },
              },
              ed(
                'Situação NFe',
                'sintegra_situacao_nfe',
                get('sintegra_situacao_nfe'),
                get('sintegra_situacao_nfe'),
              ),
              ed(
                'Código do Regime',
                'sintegra_codigo_regime',
                get('sintegra_codigo_regime'),
                get('sintegra_codigo_regime'),
              ),
              ed(
                'Regime Especial',
                'sintegra_regime_especial',
                get('sintegra_regime_especial'),
                get('sintegra_regime_especial'),
              ),
              ed(
                'Utilização do Estabelecimento',
                'sintegra_utilizacao_estabelecimento',
                get('sintegra_utilizacao_estabelecimento'),
                get('sintegra_utilizacao_estabelecimento'),
              ),
            ]}
          />

          <SubsectionLabel>Atividades secundárias</SubsectionLabel>
          <SintegraAtividadesSecundarias raw={im.sintegra_atividades_secundarias} />

          <SubsectionLabel>Endereço fiscal</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Endereço',
                'sintegra_endereco',
                get('sintegra_endereco'),
                get('sintegra_endereco'),
              ),
              ed(
                'Número',
                'sintegra_endereco_numero',
                get('sintegra_endereco_numero'),
                get('sintegra_endereco_numero'),
              ),
              ed(
                'Complemento',
                'sintegra_endereco_complemento',
                get('sintegra_endereco_complemento'),
                get('sintegra_endereco_complemento'),
              ),
              ed(
                'Bairro',
                'sintegra_bairro',
                get('sintegra_bairro'),
                formatName(get('sintegra_bairro')),
              ),
              ed(
                'Município',
                'sintegra_municipio',
                get('sintegra_municipio'),
                formatName(get('sintegra_municipio')),
              ),
              ed('UF', 'sintegra_uf', get('sintegra_uf'), get('sintegra_uf')),
              ed('CEP', 'sintegra_cep', get('sintegra_cep'), get('sintegra_cep')),
            ]}
          />

          <SubsectionLabel>Inscrições e licenças adjacentes</SubsectionLabel>
          <FieldList
            fields={[
              ed(
                'Alvará Municipal',
                'sintegra_alvara_municipal',
                get('sintegra_alvara_municipal'),
                get('sintegra_alvara_municipal'),
              ),
              ed(
                'Inscrição Municipal (ISS)',
                'sintegra_inscricao_municipal_iss',
                get('sintegra_inscricao_municipal_iss'),
                get('sintegra_inscricao_municipal_iss'),
              ),
              ed(
                'Inscrição Imobiliária',
                'sintegra_inscricao_imobiliaria',
                get('sintegra_inscricao_imobiliaria'),
                get('sintegra_inscricao_imobiliaria'),
              ),
              ed(
                'Licença Bombeiros',
                'sintegra_licenca_bombeiros',
                get('sintegra_licenca_bombeiros'),
                get('sintegra_licenca_bombeiros'),
              ),
              ed(
                'Lic. Ambiental Estadual',
                'sintegra_licenca_ambiental_estadual',
                get('sintegra_licenca_ambiental_estadual'),
                get('sintegra_licenca_ambiental_estadual'),
              ),
              ed(
                'Lic. Ambiental Municipal',
                'sintegra_licenca_ambiental_municipal',
                get('sintegra_licenca_ambiental_municipal'),
                get('sintegra_licenca_ambiental_municipal'),
              ),
              ed(
                'Lic. Vigilância Sanitária',
                'sintegra_licenca_vigilancia_sanitaria',
                get('sintegra_licenca_vigilancia_sanitaria'),
                get('sintegra_licenca_vigilancia_sanitaria'),
              ),
            ]}
          />

          <SubsectionLabel>Contador / Organização Contábil</SubsectionLabel>
          <SintegraContador raw={im.sintegra_contador} />
        </Accordion>
      </div>
    </UpdateContext.Provider>
  );
}

// ── Sub-renders SINTEGRA (read-only para campos JSONB) ──

function SintegraAtividadesSecundarias({ raw }: { raw: unknown }) {
  const list = Array.isArray(raw) ? raw : [];
  if (!list.length) {
    return <p className="text-sm text-text-muted py-2">Nenhuma atividade secundária registrada.</p>;
  }
  return (
    <ul className="text-sm text-text-2 py-2 space-y-1 list-disc pl-5">
      {list.map((it, i) => {
        const o = it as Record<string, unknown>;
        const desc = (o.descricao ?? '') as string;
        return (
          <li key={i} className="break-words">
            {desc}
          </li>
        );
      })}
    </ul>
  );
}

function SintegraContador({ raw }: { raw: unknown }) {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const entries: Array<[string, string]> = [];
  if (o.documento) entries.push(['CPF/CNPJ', String(o.documento)]);
  if (o.crc) entries.push(['CRC', String(o.crc)]);
  if (o.nome) entries.push(['Nome', String(o.nome)]);
  if (!entries.length) {
    return <p className="text-sm text-text-muted py-2">Nenhum contador vinculado.</p>;
  }
  return (
    <dl className="text-sm py-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
      {entries.map(([k, v]) => (
        <Fragment key={k}>
          <dt className="text-text-muted">{k}</dt>
          <dd className="text-text-2">{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

// ── Helpers de FieldDef ─────────────────────────────────

function ed(
  label: string,
  field: string,
  raw: string | number | null | undefined,
  display: ReactNode,
): FieldDef {
  return {
    label,
    edit: { field, raw, display },
  };
}

function edNum(
  label: string,
  field: string,
  raw: string | number | null | undefined,
  display: ReactNode,
): FieldDef {
  return {
    label,
    edit: { field, type: 'number', raw, display },
  };
}

function edDate(label: string, field: string, raw: string, display: ReactNode): FieldDef {
  return {
    label,
    edit: { field, type: 'date', raw, display },
  };
}

// ── Format helpers ──────────────────────────────────────

function safeDate(v: unknown): string {
  if (!v) return '';
  try {
    return formatDateBR(String(v));
  } catch {
    return String(v);
  }
}

function formatHa(v: unknown): string {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 4 })} ha`;
}

function formatNum(v: unknown): string {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('pt-BR');
}

function formatBRLOptional(v: unknown): string {
  if (!v) return '';
  const s = String(v);
  const n = Number(s.replace(/[^\d.,-]/g, '').replace(',', '.'));
  if (!Number.isFinite(n)) return s;
  return formatBRL(n);
}

// ── Building blocks ──────────────────────────────────────

function SubsectionLabel({ children }: { children: ReactNode }) {
  // px-3 (12px) alinha com o padding-left dos td.cli-dt-label das tables abaixo.
  return (
    <div className="text-[0.68rem] font-semibold uppercase tracking-wider text-text-muted mt-3 mb-1.5 px-3">
      {children}
    </div>
  );
}

function PartyList({
  items,
  fallback,
}: {
  items:
    | Array<{ cpf?: string | null; cpf_cnpj?: string | null; nome?: string | null }>
    | null
    | undefined;
  fallback?: { cpf: string | null; nome: string | null } | null;
}) {
  // Quando não vem nada do JSONB, cai pro dono do imóvel (cliente da ficha).
  // v7 faz a mesma coisa em ui/index.html#6371-6376.
  const list =
    Array.isArray(items) && items.length > 0
      ? items
      : fallback
        ? [{ cpf: fallback.cpf, nome: fallback.nome }]
        : [];

  if (list.length === 0) {
    return <p className="text-xs cli-empty-muted mb-3">Nenhum registro.</p>;
  }
  return (
    <table className="table table-sm w-full mb-3">
      <tbody>
        {list.map((p, i) => {
          const cpf = p.cpf || p.cpf_cnpj;
          return (
            <tr key={i}>
              <td className="cli-dt-label">CPF</td>
              <td className="tabular-nums">{cpf ? formatCpf(cpf) : '—'}</td>
              <td className="cli-dt-label">Nome</td>
              <td>{formatName(p.nome) || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// FieldGrid — 2 colunas, label + value empilhados em cada célula.
// Segue o padrão da Field primitive (mesmo do cadastro do cliente).
// Field com `full: true` ocupa a linha inteira.
function FieldGrid({ fields }: { fields: FieldDef[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mb-3">
      {fields.map((f, i) => (
        <div
          key={i}
          className={cn(
            'flex flex-col gap-1.5 py-2.5 border-b border-border',
            f.full && 'md:col-span-2',
          )}
        >
          <span className="text-xs font-medium text-text-2">{f.label}</span>
          <div className="text-sm text-text">{renderField(f)}</div>
        </div>
      ))}
    </div>
  );
}

// FieldList — usado dentro das seções accordion (Certidão, CAR, CCIR, etc.).
// Mesmo layout do FieldGrid: 2 colunas, label em cima, valor embaixo.
const FieldList = FieldGrid;

function renderField(field: FieldDef): ReactNode {
  if (field.group) {
    return (
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {field.group.map((g, i) => (
          <span key={i} className="inline-flex items-baseline">
            {i > 0 && <span className="text-text-muted mr-2">/</span>}
            <EditableCell {...g.edit} />
          </span>
        ))}
      </span>
    );
  }
  if (field.edit) {
    return <EditableCell {...field.edit} />;
  }
  const v = field.value;
  return v == null || v === '' ? <span className="cli-empty-muted">—</span> : v;
}

// ── EditableCell — clicável pra editar ─────────────────

function EditableCell({ field, type = 'text', raw, display, options }: EditableProps) {
  const ctx = useContext(UpdateContext);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(raw == null ? '' : String(raw));
  const [feedback, setFeedback] = useState<'ok' | 'err' | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setValue(raw == null ? '' : String(raw));
  }, [raw]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current) {
        try {
          (inputRef.current as HTMLInputElement).select?.();
        } catch {}
      }
    }
  }, [editing]);

  if (!ctx) return <span>{display || '—'}</span>;

  function flash(kind: 'ok' | 'err') {
    setFeedback(kind);
    setTimeout(() => setFeedback(null), kind === 'ok' ? 800 : 1500);
  }

  function save() {
    const original = raw == null ? '' : String(raw);
    if (value === original) {
      setEditing(false);
      return;
    }
    if (!ctx) return;
    const payload: Record<string, unknown> =
      type === 'number'
        ? { [field]: value === '' ? null : Number(value) }
        : { [field]: value === '' ? null : value };
    setSaving(true);
    ctx
      .patch(payload)
      .then(() => {
        setEditing(false);
        flash('ok');
      })
      .catch((err) => {
        console.error('patch erro:', err instanceof ApiError ? err.message : err);
        flash('err');
      })
      .finally(() => setSaving(false));
  }

  function cancel() {
    setValue(raw == null ? '' : String(raw));
    setEditing(false);
  }

  const bg =
    feedback === 'ok'
      ? 'rgba(5,150,105,.12)'
      : feedback === 'err'
        ? 'rgba(220,38,38,.12)'
        : undefined;

  // Altura padrão dos campos editáveis: 33px.
  const fieldClass =
    'h-[33px] w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 text-sm text-text ' +
    'transition-colors focus:outline-none focus:border-[var(--accent)]';

  // Modo leitura: valor + botão "Editar" que aparece no hover do campo.
  if (!editing) {
    return (
      <span
        className="group inline-flex items-center min-h-[33px] w-full px-2 -mx-2 rounded transition-colors hover:bg-surface2"
        style={{ background: bg }}
      >
        {display == null || display === '' ? (
          <span className="cli-empty-muted">Pendente</span>
        ) : (
          display
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Editar campo"
          aria-label="Editar campo"
          className="ml-auto shrink-0 hidden group-hover:inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-text-2 transition-colors hover:bg-surface3 hover:text-text focus:outline-none"
        >
          <Pencil size={12} strokeWidth={1.5} />
          Editar
        </button>
      </span>
    );
  }

  // Modo edição: campo liberado + Salvar/Cancelar abaixo (sem auto-save).
  return (
    <span className="flex flex-col gap-1.5 w-full">
      {type === 'select' && options ? (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
            else if (e.key === 'Enter') {
              e.preventDefault();
              save();
            }
          }}
          className={`${fieldClass} cursor-pointer`}
        >
          {options.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
          }}
          rows={3}
          className="w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text min-h-20 transition-colors focus:outline-none focus:border-[var(--accent)]"
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={fieldClass}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
        />
      )}
      <span className="flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={save} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
        <Button variant="secondary" size="sm" onClick={cancel} disabled={saving}>
          Cancelar
        </Button>
      </span>
    </span>
  );
}

// ── Accordion (card branco com borda — sem hover bg) ─────

export type AccordionTone = 'accent' | 'green' | 'indigo' | 'purple' | 'amber' | 'sky';

// Padrão ARA moderno: chip de ícone SEMPRE coral (accent) + ícone branco —
// sem arco-íris por tipo de documento. As chaves ficam pra não quebrar os
// call sites que ainda passam `tone`.
const TONE_BG: Record<AccordionTone, string> = {
  accent: 'var(--accent)',
  green: 'var(--accent)',
  indigo: 'var(--accent)',
  purple: 'var(--accent)',
  amber: 'var(--accent)',
  sky: 'var(--accent)',
};

const TONE_FG: Record<AccordionTone, string> = {
  accent: 'var(--on-solid)',
  green: 'var(--on-solid)',
  indigo: 'var(--on-solid)',
  purple: 'var(--on-solid)',
  amber: 'var(--on-solid)',
  sky: 'var(--on-solid)',
};

export function Accordion({
  icon: Icon,
  title,
  subtitle,
  tone = 'accent',
  onUpload,
  onOpenReport,
  onPrint,
  children,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string | null;
  tone?: AccordionTone;
  onUpload?: () => void;
  onOpenReport?: () => void;
  onPrint?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((v) => !v);
  return (
    <div className="mb-2 border border-border rounded-xl bg-elevated">
      <div
        className="flex items-center gap-3 px-3 cursor-pointer h-[70px]"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span
          className="shrink-0 inline-flex items-center justify-center rounded-[12px]"
          style={{
            width: 44,
            height: 44,
            background: TONE_BG[tone],
            color: TONE_FG[tone],
          }}
        >
          <Icon size={22} strokeWidth={2} />
        </span>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-bold text-text leading-tight">{title}</span>
          {subtitle && <span className="text-xs text-text-muted truncate">{subtitle}</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-text-muted hidden sm:inline">
            {open ? 'Mostrar menos' : 'Mostrar mais'}
          </span>
          {onUpload && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUpload();
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border text-xs font-medium text-text-2 hover:bg-surface2 transition-colors"
            >
              <Upload size={12} strokeWidth={1.5} />
              Enviar
            </button>
          )}
          {onOpenReport && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenReport();
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border text-xs font-medium text-text-2 hover:bg-surface2 transition-colors"
            >
              <FileText size={12} strokeWidth={1.5} />
              Abrir relatório
            </button>
          )}
          {onPrint && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPrint();
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border text-xs font-medium text-text-2 hover:bg-surface2 transition-colors"
            >
              <Printer size={12} strokeWidth={1.5} />
              Imprimir
            </button>
          )}
          <ChevronRight
            size={16}
            strokeWidth={1.5}
            className="text-text-muted transition-transform"
            style={{ transform: open ? 'rotate(90deg)' : undefined }}
          />
        </div>
      </div>
      {open && <div className="px-3 pb-4 pt-3 border-t border-border bg-elevated">{children}</div>}
    </div>
  );
}
